import { useState, useEffect, useRef, useCallback } from "react";
import { Play, ChevronDown, ChevronUp, AlertCircle } from "lucide-react";
import { CastButton } from "../CastButton";
import { AdBreakOverlay } from "../AdBreakOverlay";
import Hls from "hls.js";
import { useAdBreakMute } from "../../hooks/useAdBreakMute";

/**
 * Determine which video source to render based on admin's active_video_source
 * setting and available URLs. Returns "youtube" | "vcloud" | "m3u8" | "mp4" | null.
 */
const resolveVideoSource = (broadcast) => {
  const pref = broadcast.active_video_source || "auto";
  const youtubeUrl = broadcast.youtube_url || broadcast.youtube_vod_url;
  const hasYoutube = !!getYoutubeId(youtubeUrl);
  const hasVcloud = broadcast.vcloud_url && broadcast.vcloud_url.includes("/embed/");
  const hasM3u8 = !!broadcast.m3u8_url;
  // Direct MP4/video URL: check recording_url (and youtube_vod_url if it's not a YouTube link)
  const directUrl = broadcast.recording_url || (!hasYoutube && broadcast.youtube_vod_url) || "";
  const hasMp4 = !!directUrl && !getYoutubeId(directUrl);

  if (pref === "youtube" && hasYoutube) return "youtube";
  if (pref === "vcloud" && hasVcloud) return "vcloud";
  if (pref === "m3u8" && hasM3u8) return "m3u8";
  if (pref === "mp4" && hasMp4) return "mp4";

  // Auto: m3u8 first (most direct), then YouTube, then VCloud, then MP4
  if (pref === "auto") {
    if (hasM3u8) return "m3u8";
    if (hasYoutube) return "youtube";
    if (hasVcloud) return "vcloud";
    if (hasMp4) return "mp4";
  }

  // Fallback: try anything available
  if (hasM3u8) return "m3u8";
  if (hasYoutube) return "youtube";
  if (hasVcloud) return "vcloud";
  if (hasMp4) return "mp4";
  return null;
};

function getYoutubeId(url) {
  const match = url?.match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=|live\/))([^&?\s]+)/
  );
  return match ? match[1] : null;
}

// ── HLS Native Player ──
const HlsPlayer = ({ url, retryKey, onLoad, onError }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url) return;

    // Safari has native HLS support
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = url;
      video.addEventListener("loadeddata", onLoad);
      video.addEventListener("error", onError);
      video.play().catch(() => {});
      return () => {
        video.removeEventListener("loadeddata", onLoad);
        video.removeEventListener("error", onError);
      };
    }

    // Other browsers: use hls.js
    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
      });
      hlsRef.current = hls;

      hls.loadSource(url);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        onLoad();
        video.play().catch(() => {});
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            hls.startLoad();
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
          } else {
            onError();
            hls.destroy();
          }
        }
      });

      return () => {
        hls.destroy();
        hlsRef.current = null;
      };
    }

    // No support at all
    onError();
  }, [url, retryKey, onLoad, onError]);

  return (
    <video
      ref={videoRef}
      className="w-full h-full bg-black"
      controls
      autoPlay
      muted
      playsInline
      data-testid="hls-player"
    />
  );
};

export const VideoPlayer = ({ broadcast, isLive, liveScore, isExpanded, onToggleExpand }) => {
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [adBreakActive, setAdBreakActive] = useState(false);
  const [pastScheduledTime, setPastScheduledTime] = useState(false);
  const youtubeIframeRef = useRef(null);
  const vcloudIframeRef = useRef(null);
  const playerContainerRef = useRef(null);

  const source = resolveVideoSource(broadcast);
  const youtubeUrl = broadcast.youtube_url || broadcast.youtube_vod_url;
  const youtubeId = getYoutubeId(youtubeUrl);
  const vcloudUrl = broadcast.vcloud_url;
  const m3u8Url = broadcast.m3u8_url;
  const mp4Url = broadcast.recording_url || (!youtubeId && broadcast.youtube_vod_url) || "";
  const hasVideo = source !== null;

  // Check if we've passed the broadcast's scheduled start time
  useEffect(() => {
    if (!broadcast.scheduled_date || !broadcast.scheduled_time) return;
    const check = () => {
      const [hour, minute] = broadcast.scheduled_time.split(":").map(Number);
      const start = new Date(broadcast.scheduled_date + "T00:00:00");
      start.setHours(hour, minute, 0, 0);
      setPastScheduledTime(Date.now() >= start.getTime());
    };
    check();
    const interval = setInterval(check, 15000); // Re-check every 15s
    return () => clearInterval(interval);
  }, [broadcast.scheduled_date, broadcast.scheduled_time]);

  // Pre-game loop: M3U8 broadcast, not live, not completed, and before scheduled time
  const showPregameLoop = source === "m3u8" && !isLive && !broadcast.is_completed && !pastScheduledTime;

  useEffect(() => {
    if (hasVideo) { setVideoLoading(true); setVideoError(false); }
  }, [source, retryCount, hasVideo]);

  // Auto-clear loading after timeout for iframes
  useEffect(() => {
    if (!videoLoading || !hasVideo || source === "m3u8") return;
    const timer = setTimeout(() => setVideoLoading(false), 8000);
    return () => clearTimeout(timer);
  }, [videoLoading, hasVideo, retryCount, source]);

  const handleLoad = useCallback(() => { setVideoLoading(false); setVideoError(false); }, []);
  const handleError = useCallback(() => { setVideoLoading(false); setVideoError(true); }, []);
  const handleRetry = () => setRetryCount((c) => c + 1);

  useAdBreakMute(playerContainerRef, adBreakActive);

  return (
    <div className="relative">
      <div ref={playerContainerRef} className={`aspect-video bg-black relative ${isExpanded ? "md:aspect-[21/9]" : ""}`}>
        {/* Loading overlay — skip for pre-game loop */}
        {videoLoading && hasVideo && !showPregameLoop && (
          <div className="absolute inset-0 z-10 bg-black flex items-center justify-center" data-testid="video-loading">
            <div className="text-center">
              <div className="w-10 h-10 border-2 border-zinc-700 border-t-white rounded-full animate-spin mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">Loading stream...</p>
            </div>
          </div>
        )}

        {/* Error overlay */}
        {videoError && (
          <div className="absolute inset-0 z-20 bg-black flex items-center justify-center" data-testid="video-error">
            <div className="text-center p-8">
              <AlertCircle className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400 font-medium mb-1">Unable to load video</p>
              <p className="text-zinc-600 text-sm mb-4">
                This may be due to a slow connection or the stream being unavailable.
              </p>
              <button
                onClick={handleRetry}
                className="px-4 py-2 bg-orange-500 text-black font-bold text-sm uppercase hover:bg-orange-400 transition-colors"
                data-testid="video-retry-btn"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* ── Video renderers ── */}
        {showPregameLoop ? (
          <div
            className="w-full h-full"
            onContextMenu={(e) => e.preventDefault()}
            data-testid="pregame-loop"
          >
            <video
              src={`${process.env.REACT_APP_BACKEND_URL}/api/broadcasts/assets/pregame-loop.mp4`}
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover bg-black"
              style={{ pointerEvents: 'none' }}
            />
          </div>
        ) : source === "m3u8" ? (
          <HlsPlayer url={m3u8Url} retryKey={retryCount} onLoad={handleLoad} onError={handleError} />
        ) : source === "youtube" ? (
          <iframe
            ref={youtubeIframeRef}
            key={`yt-${retryCount}`}
            src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1&mute=1&enablejsapi=1&origin=${window.location.origin}`}
            title="Game Video"
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            onLoad={handleLoad}
            onError={handleError}
            data-testid="youtube-player"
          />
        ) : source === "vcloud" ? (
          <iframe
            ref={vcloudIframeRef}
            key={`vc-${retryCount}`}
            src={`${vcloudUrl}${vcloudUrl.includes("?") ? "&" : "?"}autoplay=1`}
            title="Game Video - VCloud"
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
            onLoad={handleLoad}
            onError={handleError}
            data-testid="vcloud-player"
          />
        ) : source === "mp4" ? (
          <video
            key={`mp4-${retryCount}`}
            src={mp4Url}
            controls
            autoPlay
            playsInline
            className="w-full h-full bg-black"
            onLoadedData={handleLoad}
            onError={handleError}
            data-testid="mp4-player"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center" data-testid="no-video">
            <div className="text-center p-8">
              <Play className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-500">Video not available</p>
              {!broadcast.is_live && !broadcast.is_completed && (
                <p className="text-zinc-600 text-sm mt-2">Check back when the game starts</p>
              )}
            </div>
          </div>
        )}

        {/* Live score overlay */}
        {isLive && liveScore && (
          <div className="absolute top-4 left-4 bg-black/80 backdrop-blur-sm px-3 py-2 rounded flex items-center gap-3">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            <span className="text-white font-bold text-sm">
              {liveScore.away_score} - {liveScore.home_score}
            </span>
            {liveScore.period && <span className="text-zinc-400 text-xs">{liveScore.period}</span>}
          </div>
        )}

        {/* Ad break overlay — works for live AND VOD */}
        {broadcast?.id && (
          <AdBreakOverlay broadcastId={broadcast.id} onActiveChange={(active) => setAdBreakActive(active)} />
        )}

        {adBreakActive && (
          <div className="absolute inset-0 z-40 bg-black pointer-events-auto" data-testid="stream-blocker" />
        )}
      </div>

      {/* Top-right controls */}
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        {(youtubeId || vcloudUrl || m3u8Url) && (
          <CastButton
            videoUrl={
              source === "youtube"
                ? `https://www.youtube.com/watch?v=${youtubeId}`
                : source === "m3u8"
                ? m3u8Url
                : vcloudUrl
            }
            title={broadcast.title || "Live Stream"}
            thumbnailUrl={broadcast.thumbnail_url}
            className="bg-black/80 hover:bg-black"
          />
        )}

        {isLive && (
          <button
            onClick={onToggleExpand}
            className="bg-black/80 hover:bg-black text-white px-3 py-1 rounded text-xs flex items-center gap-1"
          >
            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
            {isExpanded ? "Show Stats" : "Focus Mode"}
          </button>
        )}
      </div>
    </div>
  );
};
